import type {
  ActivityEntry,
  Attachment,
  Checklist,
  Comment,
  FeedItem,
  TaskDetail,
} from '@pm/schemas';
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  ErrorState,
  Input,
  Label,
  LabelCaps,
  Markdown,
  MarkdownEditor,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  PriorityBadge,
  Select,
  Skeleton,
  StatusBadge,
  Textarea,
} from '@pm/ui';
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock,
  Link2,
  Lock,
  MoreHorizontal,
  Paperclip,
  Pencil,
  Play,
  Plus,
  RotateCcw,
  Send,
  ShieldCheck,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { ContextSelect, TagPicker, UserPicker, UsersMultiPicker } from '@/components/pickers';
import { api } from '@/lib/api';
import { useAuth, useMe } from '@/lib/auth';
import {
  fmtDateTime,
  fmtMinutes,
  fmtMoney,
  fmtRelative,
  isoToLocalInput,
  localInputToIso,
  PRIORITY_LABEL,
} from '@/lib/format';
import { useFeed, useTask, useTaskMutation } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { DueCell, TaskCompactRow } from './task-bits';
import { TaskCreateDialog } from './TaskCreateDialog';
import { CustomFieldsPanel } from './CustomFieldsPanel';
import { PipelinePanel } from './PipelinePanel';
import { RecurrencePanel } from './RecurrencePanel';
import { TimePanel } from './TimePanel';
import { WorkflowPanel } from './WorkflowPanel';

/** Tela da tarefa (FRONTEND_UX §6): cabeçalho, corpo, lateral, feed. */
export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: task, isLoading, isError, error, refetch } = useTask(id);
  if (isLoading) return <DetailSkeleton />;
  if (isError || !task)
    return <ErrorState message={(error as Error)?.message} onRetry={() => void refetch()} />;
  return <Detail task={task} />;
}

function Detail({ task }: { task: TaskDetail }) {
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMe();
  const { can: authCan } = useAuth();
  const [subtaskOpen, setSubtaskOpen] = useState(false);
  const [confirm, setConfirm] = useState<null | 'cancel' | 'delete' | 'force' | 'reject'>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [title, setTitle] = useState(task.title);
  const [editingDesc, setEditingDesc] = useState(false);
  const [description, setDescription] = useState(task.description ?? '');

  const act = useTaskMutation(({ path, body }: { path: string; body?: Record<string, unknown> }) =>
    api.post<TaskDetail>(`/tasks/${task.id}/${path}`, body ?? {}),
  );
  const patch = useTaskMutation((body: Record<string, unknown>) =>
    api.patch<TaskDetail>(`/tasks/${task.id}`, body),
  );
  const del = useTaskMutation((force: { confirmation?: string } | null) =>
    force
      ? api.delete(`/tasks/${task.id}/force`, { body: force })
      : api.delete(`/tasks/${task.id}`),
  );
  const removeParticipant = useTaskMutation((userId: string) =>
    api.delete(`/tasks/${task.id}/participants/${userId}`).then(() => ({ id: task.id })),
  );

  const run = (path: string, body?: Record<string, unknown>, ok?: string) =>
    act.mutate(
      { path, body },
      { onSuccess: () => ok && toast.success(ok), onError: (e) => toast.error(e) },
    );
  const save = (body: Record<string, unknown>) =>
    patch.mutate(body, { onError: (e) => toast.error(e) });

  const can = task.can;
  const closed = task.status === 'COMPLETED' || task.status === 'CANCELED';
  const personal = task.scope === 'PERSONAL';

  const primary = (() => {
    if (task.deleted_at)
      return can.delete
        ? {
            label: 'Restaurar',
            icon: RotateCcw,
            onClick: () => run('restore', undefined, 'Tarefa restaurada'),
          }
        : null;
    if (task.status === 'PENDING_APPROVAL' && can.approve)
      return {
        label: 'Aprovar',
        icon: ShieldCheck,
        onClick: () => run('approve', {}, 'Tarefa aprovada'),
      };
    if (can.start && task.status === 'NOT_STARTED')
      return {
        label: 'Iniciar',
        icon: Play,
        onClick: () => run('start', undefined, 'Tarefa iniciada'),
      };
    if (can.complete)
      return {
        label: task.requires_approval ? 'Enviar para aprovação' : 'Concluir',
        icon: task.requires_approval ? Send : Check,
        onClick: () =>
          run(
            'complete',
            {},
            task.requires_approval ? 'Enviada para aprovação' : 'Tarefa concluída',
          ),
      };
    return null;
  })();

  return (
    <div className="flex max-w-[1200px] flex-col gap-gap-lg">
      {/* Cabeçalho */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 font-mono text-xs text-text-3">
          <Button
            variant="secondary"
            size="sm"
            className="mr-1 font-sans"
            onClick={() => {
              // Volta para onde a pessoa estava (Kanban, Meu Trabalho, cliente…); sem histórico
              // no app (link direto), sobe um nível: a mãe ou a lista.
              const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
              if (idx > 0) void navigate(-1);
              else
                void navigate(
                  task.parent ? `/tasks/${task.parent.id}` : personal ? '/pessoal' : '/demandas',
                );
            }}
          >
            <ArrowLeft /> Voltar
          </Button>
          <Link to={personal ? '/pessoal' : '/demandas'} className="hover:text-text-1">
            {personal ? 'Pessoal' : 'Demandas'}
          </Link>
          {task.parent ? (
            <>
              <ChevronRight className="size-3" />
              <Link to={`/tasks/${task.parent.id}`} className="truncate hover:text-text-1">
                #{task.parent.number} {task.parent.title}
              </Link>
            </>
          ) : null}
          <ChevronRight className="size-3" />
          <span>#{task.number}</span>
          {task.context ? (
            <>
              <span>·</span>
              <span>{task.context.name}</span>
            </>
          ) : null}
          {personal ? (
            <span className="ml-2 inline-flex items-center gap-1 text-text-3">
              <Lock className="size-3" /> PRIVADO · SÓ VOCÊ VÊ
            </span>
          ) : null}
          {task.deleted_at ? (
            <Badge tone="danger" className="ml-2">
              Na lixeira
            </Badge>
          ) : null}
        </div>

        {editingTitle && can.edit ? (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (title.trim() && title !== task.title) save({ title: title.trim() });
              setEditingTitle(false);
            }}
          >
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-10 text-xl font-semibold"
              onBlur={() => setEditingTitle(false)}
            />
          </form>
        ) : (
          <h1
            className={cn(
              'text-xl font-semibold leading-tight',
              can.edit && 'cursor-text rounded hover:bg-surface-2',
              closed && 'text-text-2',
            )}
            onClick={() => {
              if (can.edit) {
                setTitle(task.title);
                setEditingTitle(true);
              }
            }}
            title={can.edit ? 'Clique para editar' : undefined}
          >
            {task.title}
          </h1>
        )}

        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={task.status} />
          {task.workflow?.stage ? (
            <Badge mono={false} color={task.workflow.stage.color ?? undefined}>
              {task.workflow.stage.name}
            </Badge>
          ) : null}
          <PriorityBadge priority={task.priority} showNormal />
          {task.is_overdue ? (
            <Badge variant="solid" tone="danger">
              Atrasada
            </Badge>
          ) : null}
          {task.is_blocked_by_dependency ? (
            <Badge tone="warning">Aguardando dependência</Badge>
          ) : null}
          {task.requires_approval ? <Badge tone="violet">Exige aprovação</Badge> : null}
          {task.status === 'PENDING_APPROVAL' && task.approval_by_client ? (
            <Badge tone="accent">Aguardando o cliente</Badge>
          ) : null}

          <div className="ml-auto flex items-center gap-1.5">
            {task.status === 'PENDING_APPROVAL' && can.approve ? (
              <Button variant="secondary" size="sm" onClick={() => setConfirm('reject')}>
                <XCircle /> Reprovar
              </Button>
            ) : null}
            {task.status === 'IN_PROGRESS' && can.edit ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => run('set-status', { status: 'WAITING' })}
              >
                <Clock /> Aguardando
              </Button>
            ) : null}
            {primary ? (
              <Button variant="primary" size="sm" loading={act.isPending} onClick={primary.onClick}>
                <primary.icon /> {primary.label}
              </Button>
            ) : null}
            <Menu>
              <MenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="Mais ações">
                  <MoreHorizontal />
                </Button>
              </MenuTrigger>
              <MenuContent align="end">
                {task.status === 'WAITING' && can.start ? (
                  <MenuItem onSelect={() => run('start')}>
                    <Play /> Retomar
                  </MenuItem>
                ) : null}
                {task.status !== 'BLOCKED' &&
                !closed &&
                can.edit &&
                task.status !== 'PENDING_APPROVAL' ? (
                  <MenuItem onSelect={() => run('set-status', { status: 'BLOCKED' })}>
                    <Lock /> Marcar bloqueada
                  </MenuItem>
                ) : null}
                {!task.parent && can.edit ? (
                  <MenuItem onSelect={() => setSubtaskOpen(true)}>
                    <Plus /> Nova subtarefa
                  </MenuItem>
                ) : null}
                {can.complete &&
                task.visible_to_client &&
                !closed &&
                task.status !== 'PENDING_APPROVAL' ? (
                  <MenuItem
                    onSelect={() =>
                      run(
                        'request-approval',
                        { by_client: true },
                        'Aprovação do cliente solicitada (visível no portal)',
                      )
                    }
                  >
                    <ShieldCheck /> Pedir aprovação do cliente
                  </MenuItem>
                ) : null}
                <MenuItem
                  onSelect={() =>
                    void navigator.clipboard
                      .writeText(window.location.href)
                      .then(() => toast.success('Link copiado'))
                  }
                >
                  <Link2 /> Copiar link
                </MenuItem>
                <MenuSeparator />
                {can.cancel ? (
                  <MenuItem danger onSelect={() => setConfirm('cancel')}>
                    <XCircle /> Cancelar tarefa
                  </MenuItem>
                ) : null}
                {can.delete && !task.deleted_at ? (
                  <MenuItem danger onSelect={() => setConfirm('delete')}>
                    <Trash2 /> Mover para lixeira
                  </MenuItem>
                ) : null}
                {can.force_delete && task.deleted_at ? (
                  <MenuItem danger onSelect={() => setConfirm('force')}>
                    <Trash2 /> Excluir definitivamente
                  </MenuItem>
                ) : null}
              </MenuContent>
            </Menu>
          </div>
        </div>
      </div>

      <div className="grid gap-gap-lg lg:grid-cols-[1fr_300px]">
        {/* Corpo */}
        <div className="flex min-w-0 flex-col gap-gap-lg">
          <Section
            label="Descrição"
            action={
              can.edit && !editingDesc ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDescription(task.description ?? '');
                    setEditingDesc(true);
                  }}
                >
                  <Pencil /> Editar
                </Button>
              ) : undefined
            }
          >
            {editingDesc ? (
              <div className="flex flex-col gap-2">
                <MarkdownEditor
                  value={description}
                  onChange={setDescription}
                  autoFocus
                  minRows={8}
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => (setDescription(task.description ?? ''), setEditingDesc(false))}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    loading={patch.isPending}
                    onClick={() => (
                      save({ description: description || null }),
                      setEditingDesc(false)
                    )}
                  >
                    Salvar
                  </Button>
                </div>
              </div>
            ) : task.description ? (
              <Markdown onChange={can.edit ? (next) => save({ description: next }) : undefined}>
                {task.description}
              </Markdown>
            ) : (
              <p className="text-sm text-text-3">Sem descrição.</p>
            )}
          </Section>

          <ChecklistsSection task={task} />

          {!task.parent ? (
            <Section
              label={`Subtarefas · ${task.subtasks_completed}/${task.subtasks_total}`}
              action={
                can.edit && !closed ? (
                  <Button variant="ghost" size="sm" onClick={() => setSubtaskOpen(true)}>
                    <Plus /> Subtarefa
                  </Button>
                ) : undefined
              }
            >
              {task.subtasks.length === 0 ? (
                <p className="text-sm text-text-3">
                  Nenhuma subtarefa. Só existem dois níveis: mãe e subtarefa.
                </p>
              ) : (
                <div className="-mx-4 -mb-4 border-t border-border">
                  {task.subtasks.map((s) => (
                    <TaskCompactRow key={s.id} task={s} />
                  ))}
                </div>
              )}
            </Section>
          ) : (
            <DependenciesSection task={task} />
          )}

          <AttachmentsSection task={task} />

          <FeedSection task={task} />
        </div>

        {/* Lateral */}
        <aside className="flex flex-col gap-4 self-start rounded-md border border-border bg-surface-1 p-4">
          {!personal ? (
            <Block label="Responsável">
              {can.assign ? (
                <UserPicker
                  value={task.responsible?.id ?? null}
                  onChange={(uid) => run('assign', { responsible_user_id: uid })}
                  size="sm"
                />
              ) : task.responsible ? (
                <span className="inline-flex items-center gap-2">
                  <Avatar name={task.responsible.name} size="sm" /> {task.responsible.name}
                </span>
              ) : (
                <span className="text-sm text-warning">Não atribuída</span>
              )}
            </Block>
          ) : null}

          {!personal ? (
            <Block
              label="Participantes"
              action={
                can.manage_participants ? (
                  <UsersMultiPicker
                    value={task.participants.map((p) => p.id)}
                    exclude={[task.responsible?.id]}
                    label=""
                    onChange={(ids) => {
                      const added = ids.find((i) => !task.participants.some((p) => p.id === i));
                      const removed = task.participants.find((p) => !ids.includes(p.id));
                      if (added) run('participants', { user_id: added });
                      if (removed)
                        removeParticipant.mutate(removed.id, { onError: (e) => toast.error(e) });
                    }}
                  />
                ) : undefined
              }
            >
              {task.participants.length === 0 ? (
                <span className="text-sm text-text-3">Ninguém ainda</span>
              ) : (
                <ul className="flex flex-col gap-1">
                  {task.participants.map((p) => (
                    <li key={p.id} className="flex items-center gap-2 text-sm">
                      <Avatar name={p.name} size="xs" />{' '}
                      <span className="flex-1 truncate">{p.name}</span>
                      {can.manage_participants ? (
                        <button
                          type="button"
                          aria-label="Remover"
                          className="text-text-3 hover:text-danger"
                          onClick={() =>
                            removeParticipant.mutate(p.id, { onError: (e) => toast.error(e) })
                          }
                        >
                          <X className="size-3" />
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </Block>
          ) : null}

          {task.source === 'CLIENT_FORM' ? (
            <Block label="Origem">
              <div className="flex flex-col gap-0.5 text-sm">
                {task.external_request_id ? (
                  <Link to={`/triagem/${task.external_request_id}`} className="hover:text-accent">
                    Solicitação externa (formulário)
                  </Link>
                ) : (
                  <span>Formulário externo</span>
                )}
                {task.requester_contact ? (
                  <span className="text-text-2">Solicitante: {task.requester_contact.name}</span>
                ) : null}
              </div>
            </Block>
          ) : null}
          {!personal ? <WorkflowPanel task={task} /> : null}
          {!personal && !task.parent ? <PipelinePanel task={task} /> : null}
          <RecurrencePanel task={task} />

          <Block label="Prazo">
            {can.edit ? (
              <Input
                type="datetime-local"
                mono
                className="h-control-sm text-sm"
                value={isoToLocalInput(task.due_at)}
                onChange={(e) => save({ due_at: localInputToIso(e.target.value) })}
              />
            ) : (
              <DueCell task={task} />
            )}
          </Block>

          <Block label="Prioridade">
            {can.edit ? (
              <Select
                value={task.priority}
                onChange={(e) => save({ priority: e.target.value })}
                className="h-control-sm text-sm"
              >
                {(['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const).map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_LABEL[p]}
                  </option>
                ))}
              </Select>
            ) : (
              <span className="text-sm">{PRIORITY_LABEL[task.priority]}</span>
            )}
          </Block>

          {!personal && !task.parent ? (
            <Block label="Contexto">
              {can.edit ? (
                <ContextSelect
                  value={task.context?.id ?? null}
                  onChange={(cid) => save({ context_id: cid })}
                />
              ) : (
                <span className="text-sm">{task.context?.name ?? '—'}</span>
              )}
            </Block>
          ) : null}

          <Block label="Tags">
            <TagPicker
              value={task.tags.map((t) => t.id)}
              onChange={(ids) => save({ tag_ids: ids })}
              canManage={authCan('tasks.manage_tags')}
              readOnly={!can.edit}
            />
          </Block>

          <Block label="Datas">
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-xs">
              <dt className="text-text-3">Criada</dt>
              <dd>{fmtDateTime(task.created_at)}</dd>
              <dt className="text-text-3">Início</dt>
              <dd>{fmtDateTime(task.started_at)}</dd>
              <dt className="text-text-3">Conclusão</dt>
              <dd>{fmtDateTime(task.completed_at)}</dd>
            </dl>
          </Block>

          <Block label="Esforço e custo">
            <EffortEditor task={task} canEdit={can.edit} onSave={save} />
          </Block>

          <TimePanel task={task} />
          <CustomFieldsPanel entity="TASK" id={task.id} canEdit={can.edit} compact />

          {!personal ? (
            <Block label="Opções">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={task.requires_approval}
                  disabled={!can.edit}
                  onCheckedChange={(v) => save({ requires_approval: v === true })}
                />{' '}
                Exige aprovação
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={task.visible_to_client}
                  disabled={!can.edit}
                  onCheckedChange={(v) => save({ visible_to_client: v === true })}
                />{' '}
                Visível ao cliente
              </label>
              {!task.parent ? (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={task.auto_complete_on_subtasks}
                    disabled={!can.edit}
                    onCheckedChange={(v) => save({ auto_complete_on_subtasks: v === true })}
                  />{' '}
                  Concluir com as subtarefas
                </label>
              ) : (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={task.is_required}
                    disabled={!can.edit}
                    onCheckedChange={(v) => save({ is_required: v === true })}
                  />{' '}
                  Obrigatória para a mãe
                </label>
              )}
            </Block>
          ) : null}

          {task.approvals.length > 0 ? (
            <Block label="Aprovações">
              <ul className="flex flex-col gap-1.5">
                {task.approvals.map((a) => (
                  <li key={a.id} className="text-sm">
                    <Badge
                      tone={
                        a.status === 'APPROVED'
                          ? 'success'
                          : a.status === 'REJECTED'
                            ? 'danger'
                            : 'violet'
                      }
                    >
                      {a.status === 'APPROVED'
                        ? 'Aprovada'
                        : a.status === 'REJECTED'
                          ? 'Reprovada'
                          : 'Pendente'}
                    </Badge>
                    <span className="ml-1.5 text-text-2">
                      {a.reviewed_by_contact
                        ? `${a.reviewed_by_contact.name} (cliente)`
                        : (a.reviewed_by?.name ?? a.requested_by?.name)}
                    </span>
                    {a.comment ? <p className="mt-0.5 text-text-3">{a.comment}</p> : null}
                  </li>
                ))}
              </ul>
            </Block>
          ) : null}

          <Block label="Criada por">
            <span className="inline-flex items-center gap-2 text-sm">
              {task.created_by ? (
                <>
                  <Avatar name={task.created_by.name} size="xs" /> {task.created_by.name}
                </>
              ) : (
                '—'
              )}
            </span>
          </Block>
        </aside>
      </div>

      <TaskCreateDialog
        open={subtaskOpen}
        onOpenChange={setSubtaskOpen}
        parent={task}
        scope={task.scope}
      />

      <ConfirmDialog
        open={confirm === 'cancel'}
        onOpenChange={() => setConfirm(null)}
        title="Cancelar tarefa"
        description="Subtarefas abertas também serão canceladas. As concluídas permanecem."
        confirmLabel="Cancelar tarefa"
        danger
        loading={act.isPending}
        onConfirm={() => run('cancel', {}, 'Tarefa cancelada')}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        onOpenChange={() => setConfirm(null)}
        title="Mover para a lixeira"
        description="A tarefa e suas subtarefas saem das listas. Você pode restaurar depois."
        confirmLabel="Mover"
        danger
        loading={del.isPending}
        onConfirm={() =>
          del.mutate(null, {
            onSuccess: () => (toast.success('Movida para a lixeira'), setConfirm(null)),
            onError: (e) => toast.error(e),
          })
        }
      />
      <ConfirmDialog
        open={confirm === 'force'}
        onOpenChange={() => setConfirm(null)}
        title="Excluir definitivamente"
        description="Esta ação não pode ser desfeita. Comentários, anexos e histórico serão apagados."
        confirmLabel="Excluir"
        confirmText={task.title}
        danger
        loading={del.isPending}
        onConfirm={() =>
          del.mutate(
            { confirmation: task.title },
            {
              onSuccess: () => (
                toast.success('Excluída'),
                navigate(personal ? '/pessoal' : '/demandas')
              ),
              onError: (e) => toast.error(e),
            },
          )
        }
      />
      <Dialog open={confirm === 'reject'} onOpenChange={() => setConfirm(null)}>
        <DialogContent
          title="Reprovar tarefa"
          description="A tarefa volta para execução. O motivo fica no histórico."
          size="sm"
        >
          <Label htmlFor="reject">Motivo</Label>
          <Textarea
            id="reject"
            autoFocus
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            className="mt-1.5"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Voltar
            </Button>
            <Button
              variant="danger"
              disabled={!rejectComment.trim()}
              loading={act.isPending}
              onClick={() =>
                act.mutate(
                  { path: 'reject', body: { comment: rejectComment } },
                  {
                    onSuccess: () => (
                      toast.success('Tarefa reprovada'),
                      setConfirm(null),
                      setRejectComment('')
                    ),
                    onError: (e) => toast.error(e),
                  },
                )
              }
            >
              Reprovar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <span className="sr-only">{me.user.name}</span>
    </div>
  );
}

function Section({
  label,
  action,
  children,
}: {
  label: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-border bg-surface-1 p-4">
      <header className="mb-3 flex items-center gap-2">
        <LabelCaps>{label}</LabelCaps>
        <span className="ml-auto">{action}</span>
      </header>
      {children}
    </section>
  );
}

function Block({
  label,
  action,
  children,
}: {
  label: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <LabelCaps>{label}</LabelCaps>
        <span className="ml-auto">{action}</span>
      </div>
      {children}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="flex max-w-[1200px] flex-col gap-4">
      <Skeleton className="w-40" />
      <Skeleton className="h-6 w-2/3" />
      <div className="flex gap-2">
        <Skeleton className="w-20" />
        <Skeleton className="w-20" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
        <Skeleton className="h-40 rounded-md" />
        <Skeleton className="h-64 rounded-md" />
      </div>
    </div>
  );
}

/* ---------- Checklists ---------- */
function ChecklistsSection({ task }: { task: TaskDetail }) {
  const toast = useToast();
  const [newList, setNewList] = useState('');
  const [newItem, setNewItem] = useState<Record<string, string>>({});
  const mut = useTaskMutation((call: () => Promise<unknown>) =>
    call().then(() => ({ id: task.id })),
  );
  const go = (call: () => Promise<unknown>) => mut.mutate(call, { onError: (e) => toast.error(e) });
  const canEdit = task.can.edit;
  const done = task.checklists.reduce(
    (n, c) => n + c.items.filter((i) => i.is_completed).length,
    0,
  );
  const total = task.checklists.reduce((n, c) => n + c.items.length, 0);

  return (
    <Section label={total ? `Checklist · ${done}/${total}` : 'Checklist'}>
      <div className="flex flex-col gap-4">
        {task.checklists.map((c: Checklist) => (
          <div key={c.id}>
            <div className="mb-1 flex items-center gap-2">
              <span className="text-sm font-medium">{c.title}</span>
              <span className="font-mono text-xs text-text-3">
                {c.items.filter((i) => i.is_completed).length}/{c.items.length}
              </span>
              {canEdit ? (
                <button
                  type="button"
                  aria-label="Remover checklist"
                  className="ml-auto text-text-3 hover:text-danger"
                  onClick={() => go(() => api.delete(`/checklists/${c.id}`))}
                >
                  <Trash2 className="size-3.5" />
                </button>
              ) : null}
            </div>
            <ul className="flex flex-col">
              {c.items.map((i) => (
                <li key={i.id} className="group flex h-7 items-center gap-2">
                  <Checkbox
                    checked={i.is_completed}
                    disabled={!canEdit}
                    onCheckedChange={(v) =>
                      go(() => api.patch(`/checklist-items/${i.id}`, { is_completed: v === true }))
                    }
                  />
                  <span
                    className={cn('flex-1 text-base', i.is_completed && 'text-text-3 line-through')}
                  >
                    {i.text}
                  </span>
                  {canEdit ? (
                    <button
                      type="button"
                      aria-label="Remover item"
                      className="text-text-3 opacity-0 hover:text-danger group-hover:opacity-100"
                      onClick={() => go(() => api.delete(`/checklist-items/${i.id}`))}
                    >
                      <X className="size-3" />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
            {canEdit ? (
              <form
                className="mt-1 flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  const t = newItem[c.id]?.trim();
                  if (!t) return;
                  go(() => api.post(`/checklists/${c.id}/items`, { text: t }));
                  setNewItem({ ...newItem, [c.id]: '' });
                }}
              >
                <Input
                  placeholder="Novo item"
                  value={newItem[c.id] ?? ''}
                  onChange={(e) => setNewItem({ ...newItem, [c.id]: e.target.value })}
                  className="h-control-sm text-sm"
                />
                <Button type="submit" variant="ghost" size="sm">
                  <Plus />
                </Button>
              </form>
            ) : null}
          </div>
        ))}
        {canEdit ? (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!newList.trim()) return;
              go(() => api.post(`/tasks/${task.id}/checklists`, { title: newList.trim() }));
              setNewList('');
            }}
          >
            <Input
              placeholder={task.checklists.length ? 'Outra checklist' : 'Título da checklist'}
              value={newList}
              onChange={(e) => setNewList(e.target.value)}
              className="h-control-sm text-sm"
            />
            <Button type="submit" variant="secondary" size="sm">
              <Plus /> Checklist
            </Button>
          </form>
        ) : task.checklists.length === 0 ? (
          <p className="text-sm text-text-3">Sem checklist.</p>
        ) : null}
      </div>
    </Section>
  );
}

/* ---------- Dependências (só subtarefas) ---------- */
function DependenciesSection({ task }: { task: TaskDetail }) {
  const toast = useToast();
  const { data: parent } = useTask(task.parent?.id);
  const [pick, setPick] = useState('');
  const mut = useTaskMutation((call: () => Promise<unknown>) =>
    call().then(() => ({ id: task.id })),
  );
  const go = (call: () => Promise<unknown>) => mut.mutate(call, { onError: (e) => toast.error(e) });
  const siblings = (parent?.subtasks ?? []).filter(
    (s) => s.id !== task.id && !task.dependencies.some((d) => d.depends_on.id === s.id),
  );

  return (
    <Section label="Dependências">
      <div className="flex flex-col gap-3">
        <div>
          <p className="mb-1 text-sm text-text-2">Esta subtarefa depende de:</p>
          {task.dependencies.length === 0 ? (
            <p className="text-sm text-text-3">Nenhuma.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {task.dependencies.map((d) => (
                <li key={d.id} className="flex items-center gap-2 text-sm">
                  {d.depends_on.status === 'COMPLETED' ? (
                    <CheckCircle2 className="size-3.5 text-success" />
                  ) : (
                    <Clock className="size-3.5 text-warning" />
                  )}
                  <Link to={`/tasks/${d.depends_on.id}`} className="hover:text-accent">
                    <span className="font-mono text-xs text-text-3">#{d.depends_on.number}</span>{' '}
                    {d.depends_on.title}
                  </Link>
                  {!d.is_required ? <Badge tone="neutral">opcional</Badge> : null}
                  {task.can.manage_dependencies ? (
                    <button
                      type="button"
                      aria-label="Remover"
                      className="ml-auto text-text-3 hover:text-danger"
                      onClick={() => go(() => api.delete(`/tasks/${task.id}/dependencies/${d.id}`))}
                    >
                      <X className="size-3" />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {task.can.manage_dependencies && siblings.length > 0 ? (
            <form
              className="mt-2 flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (!pick) return;
                go(() => api.post(`/tasks/${task.id}/dependencies`, { depends_on_task_id: pick }));
                setPick('');
              }}
            >
              <Select
                value={pick}
                onChange={(e) => setPick(e.target.value)}
                className="h-control-sm text-sm"
              >
                <option value="">Depende de…</option>
                {siblings.map((s) => (
                  <option key={s.id} value={s.id}>
                    #{s.number} {s.title}
                  </option>
                ))}
              </Select>
              <Button type="submit" variant="secondary" size="sm" disabled={!pick}>
                <Plus />
              </Button>
            </form>
          ) : null}
        </div>
        {task.dependents.length > 0 ? (
          <div>
            <p className="mb-1 text-sm text-text-2">Bloqueia:</p>
            <ul className="flex flex-col gap-1">
              {task.dependents.map((d) => (
                <li key={d.id} className="text-sm">
                  <Link to={`/tasks/${d.task.id}`} className="hover:text-accent">
                    <span className="font-mono text-xs text-text-3">#{d.task.number}</span>{' '}
                    {d.task.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Section>
  );
}

/* ---------- Anexos ---------- */
function AttachmentsSection({ task }: { task: TaskDetail }) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState<Attachment[] | null>(null);
  const load = () =>
    api
      .get<Attachment[]>(`/attachments?entity_type=TASK&entity_id=${task.id}`)
      .then(setItems)
      .catch(() => setItems([]));
  useEffect(() => {
    void load();
  }, [task.id]);

  async function upload(file: File) {
    setUploading(true);
    try {
      const { attachment, upload_url, headers } = await api.post<{
        attachment: Attachment;
        upload_url: string;
        headers: Record<string, string>;
      }>('/attachments/presign', {
        entity_type: 'TASK',
        entity_id: task.id,
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        size: file.size,
      });
      const put = await fetch(upload_url, { method: 'PUT', headers, body: file });
      if (!put.ok) throw new Error(`Upload falhou (${put.status})`);
      await api.post(`/attachments/${attachment.id}/complete`);
      toast.success('Anexo enviado', file.name);
      await load();
    } catch (e) {
      toast.error(e, 'Falha no upload');
    } finally {
      setUploading(false);
    }
  }

  return (
    <Section
      label={`Anexos${items?.length ? ` · ${items.length}` : ''}`}
      action={
        task.can.comment && !task.deleted_at ? (
          <>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void upload(f);
                e.target.value = '';
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              loading={uploading}
              onClick={() => fileRef.current?.click()}
            >
              <Paperclip /> Anexar
            </Button>
          </>
        ) : undefined
      }
    >
      {items === null ? (
        <Skeleton className="w-1/2" />
      ) : items.length === 0 ? (
        <p className="text-sm text-text-3">Nenhum anexo.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {items.map((a) => (
            <li key={a.id} className="group flex items-center gap-2 text-sm">
              <Paperclip className="size-3.5 text-text-3" />
              <button
                type="button"
                className="truncate hover:text-accent"
                onClick={() =>
                  api
                    .get<{ url: string }>(`/attachments/${a.id}/download`)
                    .then(({ url }) => window.open(url, '_blank'))
                    .catch((e) => toast.error(e))
                }
              >
                {a.filename}
              </button>
              <span className="font-mono text-xs text-text-3">{(a.size / 1024).toFixed(0)} KB</span>
              <span className="ml-auto font-mono text-xs text-text-3">{a.uploaded_by?.name}</span>
              <button
                type="button"
                aria-label="Remover"
                className="text-text-3 opacity-0 hover:text-danger group-hover:opacity-100"
                onClick={() =>
                  api
                    .delete(`/attachments/${a.id}`)
                    .then(load)
                    .catch((e) => toast.error(e))
                }
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/* ---------- Feed: comentários + atividades ---------- */
function FeedSection({ task }: { task: TaskDetail }) {
  const toast = useToast();
  const me = useMe();
  const { data: feed, isLoading } = useFeed(task.id);
  const [text, setText] = useState('');
  const [toClient, setToClient] = useState(false);
  const add = useTaskMutation((content: string) =>
    api
      .post<Comment>(`/tasks/${task.id}/comments`, {
        content_markdown: content,
        mention_user_ids: [],
        visible_to_client: toClient,
      })
      .then(() => ({ id: task.id })),
  );

  return (
    <Section label="Comentários e atividades">
      {task.can.comment && !task.deleted_at ? (
        <form
          className="mb-4 flex gap-2.5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!text.trim()) return;
            add.mutate(text.trim(), {
              onSuccess: () => setText(''),
              onError: (err) => toast.error(err),
            });
          }}
        >
          <Avatar name={me.user.name} size="lg" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <MarkdownEditor
              value={text}
              onChange={setText}
              placeholder="Escreva um comentário… (Markdown)"
              minRows={3}
            />
            <div className="flex items-center justify-end gap-3">
              {task.visible_to_client && task.scope === 'CORPORATE' ? (
                <label className="flex items-center gap-2 text-xs text-text-2">
                  <Checkbox checked={toClient} onCheckedChange={(v) => setToClient(v === true)} />
                  Visível ao cliente no portal
                </label>
              ) : null}
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={add.isPending}
                disabled={!text.trim()}
              >
                <Send /> Comentar
              </Button>
            </div>
          </div>
        </form>
      ) : null}
      {isLoading ? (
        <Skeleton className="w-2/3" />
      ) : (feed ?? []).length === 0 ? (
        <p className="text-sm text-text-3">Nada por aqui ainda.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {(feed ?? []).map((item: FeedItem) =>
            item.kind === 'comment' ? (
              <CommentItem key={item.comment.id} c={item.comment} task={task} />
            ) : (
              <ActivityItem key={item.activity.id} a={item.activity} />
            ),
          )}
        </ul>
      )}
    </Section>
  );
}

function CommentItem({ c, task }: { c: Comment; task: TaskDetail }) {
  const fromSub = c.origin_task.id !== task.id;
  return (
    <li className="flex gap-2.5">
      <Avatar name={c.author_contact?.name ?? c.author?.name ?? '?'} size="lg" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">
            {c.author_contact?.name ?? c.author?.name ?? 'Usuário removido'}
          </span>
          {c.author_contact ? <Badge tone="accent">cliente</Badge> : null}
          {c.visible_to_client && !c.author_contact ? (
            <Badge tone="info">visível ao cliente</Badge>
          ) : null}
          <span className="font-mono text-xs text-text-3">{fmtRelative(c.created_at)}</span>
          {c.is_edited ? <span className="font-mono text-[10px] text-text-3">editado</span> : null}
          {fromSub ? (
            <Link
              to={`/tasks/${c.origin_task.id}`}
              className="rounded-sm border border-border-strong px-1 font-mono text-[10px] text-text-3 hover:text-accent"
            >
              #{c.origin_task.number}
            </Link>
          ) : null}
        </div>
        <div className="mt-0.5 text-text-2">
          <Markdown>{c.content_markdown}</Markdown>
        </div>
        {c.attachments.length ? (
          <div className="mt-1 flex flex-wrap gap-2">
            {c.attachments.map((a) => (
              <span key={a.id} className="inline-flex items-center gap-1 text-xs text-text-3">
                <Paperclip className="size-3" />
                {a.filename}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </li>
  );
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

const ACTIVITY_TEXT: Record<string, (p: Record<string, unknown> | null) => string> = {
  'task.created': () => 'criou a tarefa',
  'task.updated': (p) => `alterou ${Object.keys(p ?? {}).join(', ') || 'a tarefa'}`,
  'task.assigned': (p) => (p?.to ? 'alterou o responsável' : 'removeu o responsável'),
  'task.started': () => 'iniciou a tarefa',
  'task.completed': (p) => (p?.via_approval ? 'concluiu via aprovação' : 'concluiu a tarefa'),
  'task.canceled': (p) => (p?.cascade_from ? 'cancelada junto com a mãe' : 'cancelou a tarefa'),
  'task.restored': () => 'restaurou da lixeira',
  'task.deleted': () => 'moveu para a lixeira',
  'task.approval_requested': () => 'solicitou aprovação',
  'task.approved': () => 'aprovou',
  'task.rejected': (p) => `reprovou: ${str(p?.comment)}`,
  'task.participant_added': (p) =>
    p?.inherited ? 'participante herdado da subtarefa' : 'adicionou participante',
  'task.participant_removed': () => 'removeu participante',
  'task.dependency_added': () => 'adicionou dependência',
  'task.dependency_removed': () => 'removeu dependência',
  'task.dependency_unlocked': () => 'dependência liberada',
  'task.due_changed': () => 'alterou o prazo',
  'task.pipeline_changed': (p) =>
    p?.to
      ? `moveu para o pipeline ${str(p.to)}${p?.stage ? ` (${str(p.stage)})` : ''}`
      : `tirou do pipeline ${str(p?.from)}`,
  'task.pipeline_stage_changed': (p) => `moveu para a coluna ${str(p?.to)} em ${str(p?.pipeline)}`,
  'task.priority_changed': (p) => `alterou a prioridade para ${PRIORITY_LABEL[str(p?.to)] ?? ''}`,
  'attachment.added': (p) => `anexou ${str(p?.filename) || 'arquivo'}`,
  'attachment.removed': (p) => `removeu anexo ${str(p?.filename)}`,
  'checklist.item_completed': () => 'concluiu item da checklist',
  'task.stage_changed': (p) => `moveu para a etapa ${str(p?.to_stage_name) || str(p?.to)}`,
  'task.time_tracked': (p) => {
    const sec = typeof p?.seconds === 'number' ? p.seconds : 0;
    const m = Math.round(sec / 60);
    return `registrou ${m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m} min`} de tempo${p?.source === 'MANUAL' ? ' (manual)' : ''}`;
  },
  'workflow.started': () => 'iniciou o workflow',
};

function ActivityItem({ a }: { a: ActivityEntry }) {
  const actorName = a.actor_contact
    ? `${a.actor_contact.name} (cliente)`
    : (a.actor?.name ?? 'Sistema');
  const text = ACTIVITY_TEXT[a.action]?.(a.payload) ?? a.action;
  // Ação feita por um agente (token de acesso via MCP).
  const via = typeof a.payload?._via === 'string' ? a.payload._via : null;
  return (
    <li className="flex items-center gap-2 text-sm text-text-3">
      <span className="size-1.5 rounded-full bg-border-strong" />
      <span className="min-w-0">
        <span className="text-text-2">{actorName}</span>
        {via ? <span className="font-mono text-xs text-text-3"> · via {via}</span> : null} {text}
      </span>
      <span className="ml-auto shrink-0 whitespace-nowrap font-mono text-xs">
        {fmtRelative(a.created_at)}
      </span>
    </li>
  );
}

/** Estimativa, custos: edição inline com salvamento ao sair do campo. */
function EffortEditor({
  task,
  canEdit,
  onSave,
}: {
  task: TaskDetail;
  canEdit: boolean;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [est, setEst] = useState(task.estimated_minutes?.toString() ?? '');
  const [estCost, setEstCost] = useState(task.estimated_cost ?? '');
  const [actCost, setActCost] = useState(task.actual_cost ?? '');
  const money = (v: string) => (v.trim() === '' ? null : Number(v.replace(',', '.')).toFixed(2));
  if (!canEdit) {
    return (
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-xs">
        <dt className="text-text-3">Estimado</dt>
        <dd>{fmtMinutes(task.estimated_minutes)}</dd>
        <dt className="text-text-3">Realizado</dt>
        <dd>{fmtMinutes(task.tracked_minutes)}</dd>
        <dt className="text-text-3">Custo est.</dt>
        <dd>{fmtMoney(task.estimated_cost)}</dd>
        <dt className="text-text-3">Custo real</dt>
        <dd>{fmtMoney(task.actual_cost)}</dd>
      </dl>
    );
  }
  return (
    <dl className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1 font-mono text-xs">
      <dt className="text-text-3">Estimado (min)</dt>
      <dd>
        <Input
          type="number"
          min={0}
          mono
          value={est}
          onChange={(e) => setEst(e.target.value)}
          onBlur={() => {
            const v = est === '' ? null : Number(est);
            if (v !== task.estimated_minutes) onSave({ estimated_minutes: v });
          }}
          className="h-6 text-xs"
        />
      </dd>
      <dt className="text-text-3">Realizado</dt>
      <dd>{fmtMinutes(task.tracked_minutes)}</dd>
      <dt className="text-text-3">Custo est.</dt>
      <dd>
        <Input
          type="number"
          step="0.01"
          min={0}
          mono
          value={estCost}
          onChange={(e) => setEstCost(e.target.value)}
          onBlur={() => {
            const v = money(estCost);
            if (v !== task.estimated_cost) onSave({ estimated_cost: v });
          }}
          className="h-6 text-xs"
        />
      </dd>
      <dt className="text-text-3">Custo real</dt>
      <dd>
        <Input
          type="number"
          step="0.01"
          min={0}
          mono
          value={actCost}
          onChange={(e) => setActCost(e.target.value)}
          onBlur={() => {
            const v = money(actCost);
            if (v !== task.actual_cost) onSave({ actual_cost: v });
          }}
          className="h-6 text-xs"
        />
      </dd>
    </dl>
  );
}
